import {
  Accordion,
  AccordionDetails,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Check, Close, ExpandMore } from '@mui/icons-material';
import { memo, useCallback, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import {
  AuthorizationDataVM,
  MilanPlaceVM,
  OutsideMilanPlaceVM,
} from 'front-office/view-models/authorization-vote';
import { FormikErrors, FormikTouched } from 'formik';
import MilanPlace from './milan-place';
import OutsideMilanPlace from './outside-milan-place';
import { DateTime } from 'luxon';

interface AccordionAuthorizationDataComponentProps {
  values?: AuthorizationDataVM;
  onChange: (
    key: keyof AuthorizationDataVM,
    value: AuthorizationDataVM[keyof AuthorizationDataVM]
  ) => void;
  onChangeValues: (values: AuthorizationDataVM) => void;
  disabled?: boolean;
  touched?: FormikTouched<AuthorizationDataVM>;
  errors?: FormikErrors<AuthorizationDataVM>;
}

const AccordionAuthorizationDataComponent = ({
  values,
  onChange,
  onChangeValues,
  disabled,
  touched,
  errors,
}: AccordionAuthorizationDataComponentProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(true);

  const onFieldMilanChange = useCallback(
    (key, value) => {
      onChange('milan', {
        ...(values?.milan || {}),
        [key]: value,
      });
    },
    [onChange, values]
  );

  const onFieldsMilanChange = useCallback(
    (valuesMilan) => {
      onChange('milan', {
        ...(values?.milan || {}),
        ...valuesMilan,
      });
    },
    [onChange, values]
  );

  const onFieldOutsideMilanChange = useCallback(
    (key, value) => {
      onChange('outsideMilan', {
        ...(values?.outsideMilan || {}),
        [key]: value,
      });
    },
    [onChange, values]
  );

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-authorization-data-card"
        id="panel-authorization-data-card"
        classes={{
          root: 'pmi-flex-row-reverse',
        }}
        expandIcon={<ExpandMore />}
      >
        {disabled ? undefined : errors ? <Close color="error" /> : <Check color="success" />}
        <Typography>Dati Autorizzazione al voto</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="Luogo di cura"
                id="isMilanPlace"
                value={values?.isMilanPlace || 'false'}
                onChange={() => {
                  onChangeValues({
                    isMilanPlace: !values?.isMilanPlace,
                    milan: !values?.isMilanPlace
                      ? {
                          collegioCamera: '',
                          collegioProvinciale: '',
                          collegioSenato: '',
                          description: '',
                          requestDate: DateTime.now(),
                          section: '',
                          zone: '',
                        }
                      : null,
                    outsideMilan: values?.isMilanPlace
                      ? {
                          municipality: null,
                          place: '',
                          requestDate: DateTime.now(),
                        }
                      : null,
                  });
                }}
              >
                <FormControlLabel
                  value="true"
                  disabled={disabled}
                  control={<Radio />}
                  label="Luogo di cura in Milano"
                />
                <FormControlLabel
                  value="false"
                  disabled={disabled}
                  control={<Radio />}
                  label="Luogo di cura fuori Milano"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {values?.isMilanPlace ? (
            <MilanPlace
              onChange={onFieldMilanChange}
              values={values?.milan}
              disabled={disabled}
              touched={touched?.milan as unknown as FormikTouched<MilanPlaceVM>}
              errors={errors?.milan as FormikErrors<MilanPlaceVM>}
              onChangeValues={onFieldsMilanChange}
            />
          ) : (
            <OutsideMilanPlace
              onChange={onFieldOutsideMilanChange}
              values={values?.outsideMilan}
              disabled={disabled}
              touched={touched?.outsideMilan as unknown as FormikTouched<OutsideMilanPlaceVM>}
              errors={errors?.outsideMilan as FormikErrors<OutsideMilanPlaceVM>}
            />
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

AccordionAuthorizationDataComponent.displayName = 'Authorization data component';

const AccordionAuthorizationData = memo(AccordionAuthorizationDataComponent);

export default AccordionAuthorizationData;
